import {
    AlertCircle,
    ArrowRight,
    Clock,
    MapPin,
    User
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "../utils/axiosHandler";

const RoadTestBooking = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [schools, setSchools] = useState([]);
    const [formData, setFormData] = useState({
        drivingSchool: "",
        startDate: "",
        endDate: "",
        preferredOffices: [],
        preferredTimes: [],
        anyTime: false,
        notes: "",
        // Client information for ICBC booking
        clientLastName: "",
        clientLicenseNumber: "",
    });
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [userLoading, setUserLoading] = useState(true);

    const fetchSchools = async () => {
        try {
            const response = await axios.get("/api/driving-schools/schools");
            setSchools(response.data.data.filter(school => school.isActive));
        } catch (error) {
            console.error("Error fetching schools:", error);
        }
    };

    useEffect(() => {
        // Debug logging
        console.log("RoadTestBooking useEffect:", { isAuthenticated, user });

        if (isAuthenticated === false) {
            navigate("/login");
            return;
        }

        if (isAuthenticated === true) {
            setUserLoading(false);
            fetchSchools();
        }
    }, [isAuthenticated, user, navigate]);

    // Timeout to prevent infinite loading
    useEffect(() => {
        const timeout = setTimeout(() => {
            console.log("Timeout reached, forcing userLoading to false");
            setUserLoading(false);
        }, 5000); // 5 seconds timeout

        return () => clearTimeout(timeout);
    }, []);

    // Show loading if authentication state is not determined yet
    if (isAuthenticated === null || isAuthenticated === undefined) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Checking authentication...</p>
                </div>
            </div>
        );
    }

    // Show loading if user is authenticated but user data is still loading
    if (isAuthenticated && userLoading && !user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading user information...</p>
                    <p className="text-xs text-gray-500 mt-2">
                        Auth: {String(isAuthenticated)}, User: {user ? "loaded" : "null"}
                    </p>
                    <button
                        onClick={() => setUserLoading(false)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Continue Anyway
                    </button>
                </div>
            </div>
        );
    }

    const validateForm = () => {
        const newErrors = {};

        if (!formData.drivingSchool) {
            newErrors.drivingSchool = "Please select a driving school";
        }

        if (!formData.clientLastName) {
            newErrors.clientLastName = "Please enter the student's last name";
        }

        if (!formData.clientLicenseNumber) {
            newErrors.clientLicenseNumber = "Please enter the student's license number";
        }

        if (!formData.startDate) {
            newErrors.startDate = "Please select a start date";
        }

        if (!formData.endDate) {
            newErrors.endDate = "Please select an end date";
        }

        if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (start < today) {
                newErrors.startDate = "Start date cannot be in the past";
            }

            if (end < start) {
                newErrors.endDate = "End date must be after start date";
            }

            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays > 180) {
                newErrors.endDate = "Date range cannot exceed 6 months (180 days)";
            }
        }

        if (formData.preferredOffices.length === 0) {
            newErrors.preferredOffices = "Please select one ICBC office";
        }

        if (!formData.anyTime && formData.preferredTimes.length === 0) {
            newErrors.preferredTimes = "Please select at least one preferred time or choose All times";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Debug: Check what user data we have
            console.log("Current user data:", user);
            console.log("Form data:", formData);

            const requestData = {
                drivingSchool: formData.drivingSchool,
                customer: user?._id,
                startDate: formData.startDate,
                endDate: formData.endDate,
                preferredOffices: formData.preferredOffices,
                preferredTimes: formData.anyTime ? [] : formData.preferredTimes,
                notes: formData.notes,
                // Customer info (person making the request)
                customerInfo: {
                    firstName: user?.firstName || "Not provided",
                    lastName: user?.lastName || "Not provided",
                    email: user?.email || "no-email@example.com",
                    phone: user?.phone || "",
                    licenseNumber: user?.licenseNumber || "",
                },
                // Client info (student who will take the road test)
                clientInfo: {
                    firstName: "", // Not needed for ICBC booking
                    lastName: formData.clientLastName,
                    licenseNumber: formData.clientLicenseNumber,
                },
                searchSettings: {
                    autoSearch: true,
                    searchInterval: 60,
                    maxSearchAttempts: 1000,
                    anyTime: !!formData.anyTime,
                },
            };

            console.log("Request data being sent:", requestData);

            await axios.post("/api/driving-schools/requests", requestData);
            // Navigate user to profile road-test tab after successful submission
            navigate("/profile?tab=road-test");
            return;
        } catch (error) {
            console.error("Error creating request:", error);
            setErrors({ submit: "Failed to create request. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    const handleTimeToggle = (time) => {
        setFormData(prev => ({
            ...prev,
            preferredTimes: prev.preferredTimes.includes(time)
                ? prev.preferredTimes.filter(t => t !== time)
                : [...prev.preferredTimes, time]
        }));
    };

    const timeSlots = [
        "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM",
        "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
        "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
        "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM",
    ];

    const icbcOffices = [
        "North Vancouver driver licensing",
        "Vancouver driver licensing",
        "Richmond driver licensing",
        "Burnaby driver licensing",
        "Coquitlam driver licensing",
        "Surrey driver licensing",
        "Delta driver licensing",
        "New Westminster driver licensing",
        "Port Coquitlam driver licensing",
        "Port Moody driver licensing",
        "West Vancouver driver licensing",
        "White Rock driver licensing",
    ];

    // No standalone success UI; we redirect on success

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
                        Road Test Appointment Request
                    </h1>
                    <p className="text-lg text-gray-600">
                        Request a road test appointment and we'll automatically search for available slots
                    </p>
                </div>

                <div className="bg-white shadow-lg rounded-lg p-8">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* User Information */}
                        <div className="bg-gray-50 rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <User className="w-5 h-5 mr-2" />
                                Your Information (Request Contact)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {user?.firstName || "Not provided"} {user?.lastName || ""}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email</label>
                                    <p className="mt-1 text-sm text-gray-900">{user?.email || "Not provided"}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                                    <p className="mt-1 text-sm text-gray-900">{user?.phone || "Not provided"}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">License Number</label>
                                    <p className="mt-1 text-sm text-gray-900">{user?.licenseNumber || "Not provided"}</p>
                                </div>
                            </div>
                        </div>

                        {/* Student Information for ICBC Booking */}
                        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                            <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
                                <User className="w-5 h-5 mr-2" />
                                Student Information (for ICBC Booking)
                            </h3>
                            <p className="text-sm text-blue-800 mb-4">
                                This information will be used to book the road test appointment on ICBC website.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-blue-900 mb-2">
                                        Student Last Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.clientLastName}
                                        onChange={(e) => setFormData({ ...formData, clientLastName: e.target.value })}
                                        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientLastName ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Enter student's last name"
                                    />
                                    {errors.clientLastName && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientLastName}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-blue-900 mb-2">
                                        Student License Number *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.clientLicenseNumber}
                                        onChange={(e) => setFormData({ ...formData, clientLicenseNumber: e.target.value })}
                                        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientLicenseNumber ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Enter student's BC driver's license number"
                                    />
                                    {errors.clientLicenseNumber && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientLicenseNumber}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Driving School Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Driving School
                            </label>
                            <select
                                value={formData.drivingSchool}
                                onChange={(e) => setFormData({ ...formData, drivingSchool: e.target.value })}
                                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.drivingSchool ? "border-red-500" : "border-gray-300"
                                    }`}
                            >
                                <option value="">Choose a driving school...</option>
                                {schools.map((school) => (
                                    <option key={school._id} value={school._id}>
                                        {school.name} - {school.schoolName}
                                    </option>
                                ))}
                            </select>
                            {errors.drivingSchool && (
                                <p className="mt-1 text-sm text-red-600">{errors.drivingSchool}</p>
                            )}
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                    min={new Date().toISOString().split('T')[0]}
                                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.startDate ? "border-red-500" : "border-gray-300"
                                        }`}
                                />
                                {errors.startDate && (
                                    <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.endDate ? "border-red-500" : "border-gray-300"
                                        }`}
                                />
                                {errors.endDate && (
                                    <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                                )}
                            </div>
                        </div>

                        {/* Preferred Offices (single-select) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                                <MapPin className="w-4 h-4 mr-2" />
                                Preferred ICBC Office
                            </label>
                            <select
                                value={formData.preferredOffices[0] || ""}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    preferredOffices: e.target.value ? [e.target.value] : []
                                }))}
                                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.preferredOffices ? "border-red-500" : "border-gray-300"}`}
                            >
                                <option value="">Select an ICBC office...</option>
                                {icbcOffices.map((office) => (
                                    <option key={office} value={office}>{office}</option>
                                ))}
                            </select>
                            {errors.preferredOffices && (
                                <p className="mt-1 text-sm text-red-600">{errors.preferredOffices}</p>
                            )}
                        </div>

                        {/* Preferred Times */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700 flex items-center">
                                    <Clock className="w-4 h-4 mr-2" />
                                    Preferred Times
                                </label>
                                <label className="text-sm text-gray-700 flex items-center">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                        checked={formData.anyTime}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            anyTime: e.target.checked,
                                            preferredTimes: e.target.checked ? [] : prev.preferredTimes,
                                        }))}
                                    />
                                    All times
                                </label>
                            </div>
                            <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 ${formData.anyTime ? 'opacity-50 pointer-events-none' : ''}`}>
                                {timeSlots.map((time) => (
                                    <label key={time} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.preferredTimes.includes(time)}
                                            onChange={() => handleTimeToggle(time)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            disabled={formData.anyTime}
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{time}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.preferredTimes && (
                                <p className="mt-1 text-sm text-red-600">{errors.preferredTimes}</p>
                            )}
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Additional Notes (Optional)
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Any special requirements or preferences..."
                            />
                        </div>

                        {/* Error Message */}
                        {errors.submit && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                <div className="flex">
                                    <AlertCircle className="w-5 h-5 text-red-400" />
                                    <p className="ml-3 text-sm text-red-600">{errors.submit}</p>
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        Submit Request
                                        <ArrowRight className="ml-2 w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Information Box */}
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">How it works</h3>
                    <div className="space-y-2 text-sm text-blue-800">
                        <p>1. Submit your request with your preferred date range and locations</p>
                        <p>2. Our system will automatically search for available appointments</p>
                        <p>3. When an appointment is found, you'll be notified immediately</p>
                        <p>4. The appointment will be automatically booked on your behalf</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoadTestBooking; 